import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, TextField,
  CircularProgress, Alert, Grid
} from '@mui/material';
import { emailTemplateService } from '../../services/emailTemplateService';

const EmailTemplateForm = () => {
  const { project, id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;

  const [formData, setFormData] = useState({
    name: '',
    content: ''
  });

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const data = await emailTemplateService.getEmailTemplate(id);
        setFormData({
          name: data.name || '',
          content: data.content || ''
        });
      } catch (err) {
        console.error('Error fetching email template:', err);
        setError('Failed to load email template details.');
      } finally {
        setLoading(false);
      }
    };

    if (!isNew) {
      fetchTemplate();
    }
  }, [id, isNew]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Template name is required.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload = { ...formData, project };

      if (isNew) {
        await emailTemplateService.createEmailTemplate(payload);
      } else {
        await emailTemplateService.updateEmailTemplate(id, payload);
      }

      // Navigate back to the list after successful save
      navigate(`/project/${project}/emailtemplate`);
    } catch (err) {
      console.error('Error saving email template:', err);
      setError(err.response?.data?.message || 'Failed to save email template.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h4" component="h1">
          {isNew ? 'Add new Template' : `Edit Template: ${formData.name}`}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(`/project/${project}/emailtemplate`)}>
          Cancel
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      <Paper sx={{ p: 4, maxWidth: 800 }}>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <TextField
            required
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />

          <TextField
            fullWidth
            multiline
            rows={12}
            label="Content"
            name="content"
            placeholder="Add Email Template Content (HTML or Text)"
            value={formData.content}
            onChange={handleChange}
            sx={{
              '& .MuiInputBase-root': {
                backgroundColor: '#fffdf2', // mimicking the old template background style
                fontFamily: 'monospace'
              }
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Template'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default EmailTemplateForm;
